import { StoreOrderStep } from '@/dealers/_gen/gql'
import AccountMachine from '@/gf/modules/AccountMachine'
import AdditionalCharge from '@/gf/modules/AdditionalCharge'
import Discount from '@/gf/modules/Discount'
import Invoice from '@/gf/modules/Invoice'
import LineItem from '@/gf/modules/LineItem'
import Money from '@/gf/modules/Money'
import Refund from '@/gf/modules/Refund'
import Shipment from '@/gf/modules/Shipment'
import StoreOrderAudit from '@/gf/modules/StoreOrderAudit'
import Time from '@/gf/modules/Time'
import Transaction from '@/gf/modules/Transaction'
import Vendor from '@/gf/modules/Vendor'
import { QueryHookOptions, gql, useQuery } from '@apollo/client'
import { Pagination, StoreOrderEventPayload, StoreOrderPayload } from '../../types'

interface Data {
  storeOrdersSearch: {
    storeOrders: (Omit<StoreOrderPayload, 'order' | 'events'> & {
      step: StoreOrderStep
      order: Omit<StoreOrderPayload['order'], 'user'> & {
        user: {
          id: string
          displayName: string
          email: string
          phoneNumber: string | null
        }
      }
      events: (Omit<StoreOrderEventPayload, 'user'> & {
        user: { id: string; displayName: string }
      })[]
    })[]
    pagination: Pagination
  }
}

interface Variables {
  page?: number
  filters?: string[][]
}

const query = gql`
  query SearchStoreOrders($page: Int, $filters: [[String]]) {
    storeOrdersSearch(page: $page, filters: $filters) {
      storeOrders {
        id
        shortId
        insertedAt
        updatedAt
        state
        step
        deliveryMethod
        paymentStatus
        shippingAmount {
          amount
          currency
        }
        salesTax {
          amount
          currency
        }
        convenienceFeeRate
        gearflowShipping
        purchaseOrder
        payOnInvoice
        timingDetails
        shippingAddress {
          firstName
          lastName
          lineOne
          lineTwo
          city
          state
          country
          postalCode
          companyName
          deliverable
          rdi
        }
        vendor {
          accountNumbers
          contactName
          emailAddress
          phoneNumber
        }
        accountMachines {
          id
          name
          serialNumber
          owned
          engineMake
          engineModel
          engineSerialNumber
          machine {
            make
            model
            year
          }
        }
        store {
          id
          name
          address {
            firstName
            lastName
            lineOne
            lineTwo
            city
            state
            country
            postalCode
            companyName
          }
          phoneNumber
          shipsFree
          gearflowPaymentsEnabled
        }
        order {
          id
          name
          shortId
          insertedAt
          updatedAt
          state
          phoneNumber
          email
          comment
          user {
            id
            displayName
            email
            phoneNumber
          }
          billingAddress {
            firstName
            lastName
            lineOne
            lineTwo
            city
            state
            country
            postalCode
            companyName
          }
          requestForQuote {
            id
            fullName
            phoneNumber
            emailAddress
            partsRequest
            machineInformation
          }
        }
        balanceTransaction {
          id
        }
        additionalCharges {
          id
          name
          price {
            amount
            currency
          }
          state
        }
        discounts {
          percent
        }
        refunds {
          id
          amount {
            amount
            currency
          }
        }
        lineItems {
          id
          quantity
          product {
            id
            name
            mpn
            salePrice
            images {
              variants {
                name
                url
              }
            }
          }
          unitPrice {
            amount
            currency
          }
          discountedUnitPrice {
            amount
            currency
          }
          extendedPrice {
            amount
            currency
          }
          shipmentItems {
            id
            quantity
            shipment {
              id
              link
              carrier
              tracking
              shippedAt
              label {
                id
                url
                cost {
                  amount
                  currency
                }
                shipDate
              }
            }
          }
        }
        events {
          id
          type
          privacy
          user {
            id
            displayName
          }
          insertedAt
          transaction {
            id
            action
            customer {
              amount
              currency
            }
            gearflow {
              amount
              currency
            }
            supplier {
              amount
              currency
            }
          }
          refund {
            id
            amount {
              amount
              currency
            }
          }
          additionalCharge {
            id
            name
            price {
              amount
              currency
            }
          }
          shipment {
            id
            shipmentItems {
              id
              link
              carrier
              tracking
              quantity
              lineItem {
                id
                productName
                quantity
              }
            }
          }
          storeOrderAudit {
            id
            shippingCost {
              amount
              currency
            }
            timingDetails
            pickup
            taxCost {
              amount
              currency
            }
            lineItemAudits {
              id
              quantity
              unitPrice {
                amount
                currency
              }
              productId
              productName
              productMpn
            }
          }
          diffStoreOrderAudit {
            id
            shippingCost {
              amount
              currency
            }
            timingDetails
            pickup
            taxCost {
              amount
              currency
            }
            lineItemAudits {
              id
              quantity
              unitPrice {
                amount
                currency
              }
              productId
              productName
              productMpn
            }
          }
          note
          emailSent
          data {
            id
            details
            payOnAccount
            payOnInvoice
            deliveryMethod
            previousAddress {
              firstName
              lastName
              lineOne
              lineTwo
              city
              state
              country
              postalCode
              companyName
            }
          }
        }
        features {
          txns
          storeOrderTimelineV1
        }
      }
      pagination {
        totalPages
        totalResults
      }
    }
  }
`

const useSearchStoreOrders = (options?: QueryHookOptions<Data, Variables>) => {
  const result = useQuery<Data, Variables>(query, options)

  const data = result.data && {
    ...result.data,
    storeOrdersSearch: {
      ...result.data.storeOrdersSearch,
      storeOrders: result.data.storeOrdersSearch.storeOrders.map((payload) => ({
        ...payload,
        shippingAmount: payload.shippingAmount && Money.fromPayload(payload.shippingAmount),
        salesTax: payload.salesTax && Money.fromPayload(payload.salesTax),
        lineItems: payload.lineItems && payload.lineItems.map(LineItem.fromPayload),
        additionalCharges:
          payload.additionalCharges && payload.additionalCharges.map(AdditionalCharge.fromPayload),
        refunds: payload.refunds && payload.refunds.map(Refund.fromPayload),
        discounts: payload.discounts && payload.discounts.map(Discount.fromPayload),
        events: payload.events.map((event) => ({
          ...event,
          transaction: event.transaction && Transaction.fromPayload(event.transaction),
          refund: event.refund ? Refund.fromPayload(event.refund) : undefined,
          additionalCharge: event.additionalCharge
            ? AdditionalCharge.fromPayload(event.additionalCharge)
            : undefined,
          storeOrderAudit: event.storeOrderAudit
            ? StoreOrderAudit.fromPayload(event.storeOrderAudit)
            : undefined,
          diffStoreOrderAudit: event.diffStoreOrderAudit
            ? StoreOrderAudit.fromPayload(event.diffStoreOrderAudit)
            : undefined,
          shipment: event.shipment ? Shipment.fromPayload(event.shipment) : undefined,
          insertedAt: Time.fromPayload(event.insertedAt),
        })),
        insertedAt: Time.fromPayload(payload.insertedAt),
        updatedAt: Time.fromPayload(payload.updatedAt),
        accountMachines:
          payload.accountMachines &&
          payload.accountMachines.map((accountMachinePayload) =>
            AccountMachine.fromPayload(accountMachinePayload)
          ),
        vendor: payload.vendor && Vendor.fromPayload(payload.vendor),
        invoices:
          payload.invoices && payload.invoices.map((invoice) => Invoice.fromPayload(invoice)),
        total: Money.fromPayload(payload.total),
      })),
    },
  }

  return { ...result, data }
}

export default useSearchStoreOrders
